import {
  OrderProductInventoryAvailability,
  OrderProductInventorySkuAvailability,
  OrderProductInventorySkuWarehouseStock,
  OrderProductWarehouseStock,
} from "../../../models/StockIssueRequest/OrderInventoryAvailability";
import { OrderProductsVariantSchema } from "../../../models/Order/OrderProductsVariant";
import {
  ProductAvailableQuantity,
  ProductStockDetails,
  VariantStock,
} from "../../../models/SwageazyInventory/SwageazyInventory";
import { OrderProduct } from "../../../models/Order/OrderProduct";

export function sumTotalStockAvailable(
  totalStock: ProductStockDetails["total_stock"],
): number {
  return (totalStock ?? []).reduce(
    (sum, row) => sum + Math.max(0, Number(row.quantity ?? 0)),
    0,
  );
}

export function aggregateSkuAvailableQty(
  variantsStock: VariantStock[] | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  (variantsStock ?? []).forEach((row) => {
    const key = String(row.sku ?? "")
      .trim()
      .toUpperCase();
    if (!key) return;
    map.set(key, (map.get(key) ?? 0) + Math.max(0, Number(row.quantity ?? 0)));
  });
  return map;
}

export function aggregateProductWarehouseStock(
  totalStock: ProductStockDetails["total_stock"],
): OrderProductWarehouseStock[] {
  const map = new Map<string, OrderProductWarehouseStock>();

  (totalStock ?? []).forEach((row) => {
    const warehouseId = String(row.warehouse ?? "").trim();
    if (!warehouseId) return;

    const qty = Math.max(0, Number(row.quantity ?? 0));
    if (qty <= 0) return;

    const existing = map.get(warehouseId);
    if (existing) {
      existing.quantity += qty;
    } else {
      map.set(warehouseId, {
        warehouse_id: warehouseId,
        city: row.city ?? null,
        quantity: qty,
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => b.quantity - a.quantity);
}

export function buildSkuWarehouseLocations(
  sku: string,
  variantsStock: VariantStock[] | undefined,
): OrderProductInventorySkuWarehouseStock[] {
  const skuKey = String(sku ?? "")
    .trim()
    .toUpperCase();
  if (!skuKey) return [];

  const map = new Map<string, OrderProductInventorySkuWarehouseStock>();

  (variantsStock ?? []).forEach((row) => {
    if (
      String(row.sku ?? "")
        .trim()
        .toUpperCase() !== skuKey
    ) {
      return;
    }

    const warehouseId = String(row.warehouse ?? "").trim();
    if (!warehouseId) return;

    const qty = Math.max(0, Number(row.quantity ?? 0));
    if (qty <= 0) return;

    const existing = map.get(warehouseId);
    if (existing) {
      existing.quantity += qty;
    } else {
      map.set(warehouseId, {
        warehouse_id: warehouseId,
        city: row.city ?? null,
        quantity: qty,
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => b.quantity - a.quantity);
}

export function formatWarehouseLabel(
  location: Pick<OrderProductWarehouseStock, "warehouse_id">,
  resolveWarehouseName?: (warehouseId: string) => string,
): string {
  return resolveWarehouseName?.(location.warehouse_id) || location.warehouse_id;
}

export function formatWarehouseStockSummary(
  locations: OrderProductWarehouseStock[] | undefined,
  resolveWarehouseName?: (warehouseId: string) => string,
): string {
  if (!locations?.length) return "";

  return locations
    .map(
      (loc) =>
        `${formatWarehouseLabel(loc, resolveWarehouseName)}: ${loc.quantity}`,
    )
    .join(" · ");
}

export function buildOrderProductInventoryFromStockDetails(params: {
  product: OrderProduct;
  stockDetails: ProductStockDetails;
}): OrderProductInventoryAvailability {
  const warehouseStock = aggregateProductWarehouseStock(
    params.stockDetails.total_stock,
  );

  return {
    product_id: params.product.id,
    product_name: params.product.name ?? null,
    ordered_quantity: Number(params.product.quantity ?? 0),
    total_available_quantity: warehouseStock.reduce(
      (sum, row) => sum + row.quantity,
      0,
    ),
    warehouse_stock: warehouseStock,
    sku_variants: [],
  };
}

export function buildOrderProductInventoryTotals(params: {
  products: OrderProduct[];
  availableQuantities: ProductAvailableQuantity[];
}): Record<number, OrderProductInventoryAvailability> {
  const availableMap = new Map<number, number>();
  params.availableQuantities.forEach((row) => {
    availableMap.set(row.product_id, Math.max(0, Number(row.available_quantity ?? 0)));
  });

  return params.products.reduce(
    (acc, product) => {
      acc[product.id] = {
        product_id: product.id,
        product_name: product.name ?? null,
        ordered_quantity: Number(product.quantity ?? 0),
        total_available_quantity: availableMap.get(product.id) ?? 0,
        warehouse_stock: [],
        sku_variants: [],
      };
      return acc;
    },
    {} as Record<number, OrderProductInventoryAvailability>,
  );
}

export function buildOrderProductSkuAvailability(params: {
  orderVariants: OrderProductsVariantSchema[];
  variantsStock: VariantStock[] | undefined;
}): OrderProductInventorySkuAvailability[] {
  const skuAvailableMap = aggregateSkuAvailableQty(params.variantsStock);

  return params.orderVariants
    .filter((variant) => Boolean(variant.sku))
    .map((variant) => {
      const skuKey = String(variant.sku).trim().toUpperCase();
      return {
        variant_id: variant.id ?? null,
        sku: variant.sku,
        title: variant.title ?? null,
        ordered_quantity: Number(variant.quantity ?? 0),
        available_quantity: skuAvailableMap.get(skuKey) ?? 0,
        warehouse_locations: buildSkuWarehouseLocations(
          variant.sku,
          params.variantsStock,
        ),
      };
    });
}

export function buildOrderProductInventoryAvailability(params: {
  product_id: number;
  product_name: string | null;
  ordered_quantity: number;
  stockDetails: ProductStockDetails;
  orderVariants: OrderProductsVariantSchema[];
}): OrderProductInventoryAvailability {
  const skuVariants = buildOrderProductSkuAvailability({
    orderVariants: params.orderVariants,
    variantsStock: params.stockDetails.variants_stock,
  });

  return {
    product_id: params.product_id,
    product_name: params.product_name,
    ordered_quantity: params.ordered_quantity,
    total_available_quantity: sumTotalStockAvailable(
      params.stockDetails.total_stock,
    ),
    warehouse_stock: aggregateProductWarehouseStock(
      params.stockDetails.total_stock,
    ),
    sku_variants: skuVariants,
  };
}

export type ProductInventoryAvailabilityTone =
  | "neutral"
  | "success"
  | "warning"
  | "error";

export interface ProductInventoryAvailabilityDisplay {
  shortLabel: string;
  tooltip: string;
  tone: ProductInventoryAvailabilityTone;
}

export function getProductInventoryAvailabilityDisplay(params: {
  availability: OrderProductInventoryAvailability | null;
  isLoading: boolean;
}): ProductInventoryAvailabilityDisplay {
  if (params.isLoading) {
    return {
      shortLabel: "Checking…",
      tooltip: "Loading Swageazy warehouse availability",
      tone: "neutral",
    };
  }

  if (!params.availability) {
    return {
      shortLabel: "Not checked",
      tooltip: "Warehouse availability has not been loaded yet",
      tone: "neutral",
    };
  }

  const available = Math.max(
    0,
    Number(params.availability.total_available_quantity ?? 0),
  );
  const ordered = Math.max(0, Number(params.availability.ordered_quantity ?? 0));

  if (available <= 0) {
    return {
      shortLabel: "Out of stock",
      tooltip:
        ordered > 0
          ? `No units in Swageazy warehouse (order line qty: ${ordered})`
          : "No units in Swageazy warehouse",
      tone: "error",
    };
  }

  if (ordered > 0 && available >= ordered) {
    return {
      shortLabel: `In stock · ${available}`,
      tooltip: `${available} units available in Swageazy warehouse (order needs ${ordered})`,
      tone: "success",
    };
  }

  if (ordered > 0 && available < ordered) {
    return {
      shortLabel: `Partial · ${available}/${ordered}`,
      tooltip: `Only ${available} of ${ordered} units available in Swageazy warehouse`,
      tone: "warning",
    };
  }

  return {
    shortLabel: `${available} in warehouse`,
    tooltip: `${available} units available in Swageazy warehouse`,
    tone: "neutral",
  };
}

export function availabilityToneColor(
  tone: ProductInventoryAvailabilityTone,
): string {
  switch (tone) {
    case "success":
      return "#16A34A";
    case "warning":
      return "#D97706";
    case "error":
      return "#DC2626";
    default:
      return "#2563EB";
  }
}
